import React, { ReactElement, useCallback } from 'react';

import DashboardHeader from 'adapter/in/dashboards/pages/Dashboard/Header';
import Dashboard from 'domain/entity/dashboard';
import { useNavigation } from 'adapter/in/common/hooks/useNavigation';
import { useBranding } from 'adapter/in/common/context/static/Branding';
import i18n from 'asset/translation';
import { dispatchAlertToaster } from 'adapter/in/common/context/Alert';
import { useDashboard } from 'adapter/in/dashboards/context/Dashboard';
import { GetDashboardCriteriaBuilder } from 'use-case/dashboard/get/getDashboardCriteria';
import { dashboardsPath } from 'adapter/in/common/routes';

export type DashboardPageHeaderContainerProps = {
    dashboard: Dashboard;
    onFullscreen: () => void;
    fullscreenActive: boolean;
};

export const useDashboardPageHeaderContainer = (
    dashboardId: string
): {
    handleHeaderOnBack: () => void;
    onDeleteSuccess: () => void;
    onDuplicateSuccess: () => void;
    onShareSuccess: () => void;
    onPinSuccess: (pinned: boolean) => void;
} => {
    const { navigationRoot } = useNavigation();
    const { get: getDashboard } = useDashboard();
    const handleHeaderOnBack = useCallback(() => navigationRoot(dashboardsPath), []);
    const { setSuccessAlert } = dispatchAlertToaster();
    const onDeleteSuccess = () => {
        navigationRoot(dashboardsPath);
    };
    const onDuplicateSuccess = () => {
        setSuccessAlert(i18n.t('dashboards.duplicate.modal.success'));
    };

    const onShareSuccess = () => {
        getDashboard(GetDashboardCriteriaBuilder.create().withId(dashboardId).build());
    };

    const onPinSuccess = (pinned: boolean) => {
        setSuccessAlert(pinned ? i18n.t('dashboards.pinned.modal.unpin.success') : i18n.t('dashboards.pinned.modal.pin.success'));
        getDashboard(GetDashboardCriteriaBuilder.create().withId(dashboardId).build());
    };

    return {
        handleHeaderOnBack,
        onDeleteSuccess,
        onDuplicateSuccess,
        onShareSuccess,
        onPinSuccess,
    };
};

const DashboardPageHeaderContainer = (props: DashboardPageHeaderContainerProps): ReactElement => {
    const { dashboard, onFullscreen, fullscreenActive } = props;
    const { handleHeaderOnBack, onDeleteSuccess, onDuplicateSuccess, onShareSuccess, onPinSuccess } = useDashboardPageHeaderContainer(
        dashboard.id
    );
    const { branding } = useBranding();

    return (
        <DashboardHeader
            dashboard={dashboard}
            pinned={dashboard.pinned}
            branding={branding}
            onBack={handleHeaderOnBack}
            onFullscreen={onFullscreen}
            fullscreenActive={fullscreenActive}
            onDeleteSuccess={onDeleteSuccess}
            onDuplicateSuccess={onDuplicateSuccess}
            onShareSuccess={onShareSuccess}
            onPinSuccess={onPinSuccess}
        />
    );
};

export default DashboardPageHeaderContainer;
